import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button } from '@/components/ui';
import { BrandMark } from './FullPageLoader';

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes so one broken screen never blanks the whole console.
 * Route-level failures are handled by RouteErrorBoundary below; this wraps the app.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Replace with a real error reporter when one is wired up.
    console.error('[Energise] unhandled render error', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <CrashScreen
        title="Something broke on this screen"
        detail={this.state.error.message}
        onReset={() => this.setState({ error: null })}
      />
    );
  }
}

/** react-router's errorElement — also handles thrown 404/500 responses. */
export function RouteErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <CrashScreen
        title={error.status === 404 ? 'Page not found' : `Error ${error.status}`}
        detail={error.statusText || 'The page could not be loaded.'}
      />
    );
  }

  return (
    <CrashScreen
      title="Something broke on this screen"
      detail={error instanceof Error ? error.message : 'An unexpected error occurred.'}
    />
  );
}

function CrashScreen({
  title,
  detail,
  onReset,
}: {
  title: string;
  detail?: string;
  onReset?: () => void;
}) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
      <BrandMark className="h-10 w-10" />
      <div className="space-y-1">
        <h1 className="text-lg font-semibold text-fg">{title}</h1>
        {detail ? <p className="max-w-md text-sm text-muted">{detail}</p> : null}
      </div>
      <div className="flex gap-2">
        {onReset ? (
          <Button variant="secondary" onClick={onReset}>
            Try again
          </Button>
        ) : null}
        <Button variant="primary" onClick={() => window.location.assign('/')}>
          Back to dashboard
        </Button>
      </div>
    </div>
  );
}
